import { useEffect, useMemo } from "react"
import { TimelinePlayer } from "@/components/timeline-player"
import { cacheDesktopMedia } from "@/lib/desktop"
import { buildSingleVideoDocument, MIN_CLIP_MS, type EngineSources, type TimelineSource } from "@/lib/timeline"
import { filmstripUrlsForRange } from "@/lib/timeline/plan-filmstrip-cells"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"

export type ShotAnimaticSegmentPlayerProps = {
  segmentId: number
  startMs: number
  endMs: number
  /** Prefer with-audio, then silent reference clip (same-origin proxy). */
  referenceClipStreamUrl: string | null
  /** Full animatic proxy URL for trim fallback when no reference clip. */
  animaticVideoStreamUrl: string | null
  /** Кадры исходного файла (не референс-клипа) — покадровка на таймлайне. */
  timelineFrameUrls?: string[]
  timelineFrameAspectRatio?: number | null
  /** Длительность исходного файла, из которого нарезаны кадры. */
  sourceDurationMs?: number | null
  frameRate?: number
  aspectRatio?: number | null
  className?: string
}

/**
 * Read-only TimelinePlayer for shot panel animatic tab:
 * reference clip as full doc, or trimmed window on full animatic.
 */
export function ShotAnimaticSegmentPlayer({
  segmentId,
  startMs,
  endMs,
  referenceClipStreamUrl,
  animaticVideoStreamUrl,
  timelineFrameUrls = [],
  timelineFrameAspectRatio = null,
  sourceDurationMs = null,
  frameRate = DEFAULT_VIDEO_OUTPUT_FPS,
  aspectRatio = null,
  className,
}: ShotAnimaticSegmentPlayerProps) {
  const fps = frameRate > 0 ? frameRate : DEFAULT_VIDEO_OUTPUT_FPS
  const spanMs = Math.max(0, endMs - startMs)
  const trustedSourceMs = sourceDurationMs != null && sourceDurationMs > 0 ? sourceDurationMs : 0
  // Полный файл (start=0): если окно сегмента ещё не приехало, берём duration исходника,
  // иначе клип остаётся MIN_CLIP_MS на линейке в десятки секунд.
  const usedSourceFallback = startMs === 0 && trustedSourceMs > 0 && spanMs < MIN_CLIP_MS
  const clipDurationMs = usedSourceFallback ? trustedSourceMs : Math.max(1, spanMs)
  const sourceOutMs = usedSourceFallback ? startMs + clipDurationMs : endMs
  const useReference = Boolean(referenceClipStreamUrl)
  const streamUrl = referenceClipStreamUrl ?? animaticVideoStreamUrl
  const sourceId = segmentId
  const filmstripAspect = timelineFrameAspectRatio ?? aspectRatio

  // Диск-кэш Electron: вкладка играет референс-клип или полный файл аниматика.
  useEffect(() => {
    cacheDesktopMedia([animaticVideoStreamUrl, referenceClipStreamUrl])
  }, [animaticVideoStreamUrl, referenceClipStreamUrl])

  const doc = useMemo(() => {
    if (useReference) {
      return buildSingleVideoDocument({ sourceId, durationMs: clipDurationMs })
    }
    return buildSingleVideoDocument({
      sourceId,
      durationMs: clipDurationMs,
      sourceInMs: startMs,
      sourceOutMs,
    })
  }, [useReference, sourceId, clipDurationMs, startMs, sourceOutMs])

  const engineSources = useMemo<EngineSources>(() => {
    if (!streamUrl) return { versionUrls: {}, assetUrls: {} }
    return { versionUrls: { [sourceId]: streamUrl }, assetUrls: {} }
  }, [streamUrl, sourceId])

  const timelineSources = useMemo<Record<string, TimelineSource>>(() => {
    if (!streamUrl) return {}
    // Референс-клип начинается с нуля, а кадры нарезаны по исходному файлу —
    // вырезаем окно сегмента, чтобы покадровка совпала с тем, что играет.
    const frameUrls = useReference
      ? filmstripUrlsForRange(timelineFrameUrls, sourceDurationMs ?? sourceOutMs, startMs, sourceOutMs)
      : timelineFrameUrls
    const sourceDurationForLane = !useReference && trustedSourceMs > 0 ? trustedSourceMs : clipDurationMs
    return {
      [String(sourceId)]: {
        version_id: sourceId,
        stream_url: streamUrl,
        duration_ms: sourceDurationForLane,
        frame_rate: fps,
        aspect_ratio: filmstripAspect,
        timeline_frame_urls: frameUrls,
      },
    }
  }, [
    streamUrl,
    sourceId,
    useReference,
    clipDurationMs,
    sourceOutMs,
    startMs,
    fps,
    filmstripAspect,
    timelineFrameUrls,
    sourceDurationMs,
    trustedSourceMs,
  ])

  if (!streamUrl) return null

  return (
    <TimelinePlayer
      key={`${sourceId}-${useReference ? "ref" : "trim"}-${startMs}-${sourceOutMs}`}
      doc={doc}
      sources={engineSources}
      timelineSources={timelineSources}
      aspectRatio={filmstripAspect}
      frameRate={fps}
      mediaDurationMs={clipDurationMs}
      defaultLoop
      isDefaultDoc={useReference}
      showTrackHeaders={false}
      className={className}
    />
  )
}
